import UploadSelectorFileUploadModel from '@/VueComponents/FileUploadSelector/models/uploadSelectorFileUploadModel';
import UploadSelectorFolderUploadModel from '@/VueComponents/FileUploadSelector/models/uploadSelectorFolderUploadModel';
import UploadService from '@/Services/uploadService';
import { useContextDataStore } from '@/VueCore/stores/contextDataStore';
import { UploadParentItemType } from '@/Types/Enums/UploadParentItemType';
import BaseUploadModel from '@/Models/baseUploadModel';
import { groupUploadsToFolders, isUploadBelongsToFolder } from '@/VueComponents/Uploads/utils/uploadsUtils';
import UploadModel from '@/Models/uploadModel';
import UploadTypeResolver from '@/Utils/uploadTypeResolver';

export default function useFileUploader(uploads: BaseUploadModel[],
  requestId: string,
  requestType: UploadParentItemType) {

  const metadataAvailable = requestType !== UploadParentItemType.businessRequest;

  // TODO: Remove when partner request upload file selector is reworked to use new generic FileSelector components
  function uploadFilesObsolete(fileList: FileList | File[], errorCallback: (e:any) => void) {

    const files = Array.from(fileList);
    const uploadModels: UploadModel[] =
      files.map(f => UploadModel.fromFile(f, UploadTypeResolver.getTypeFromNativeFile(f)));

    const addedUploads = populateWithNewUploadModels(uploadModels);

    // Asynchronously create each upload on the server / generate upload token then start the uploads
    UploadService.authorizeUploads(uploadModels, requestId, requestType, metadataAvailable)
      .catch(e => {
        addedUploads.forEach(u => u.setFailedAuthorization());
        errorCallback(e);
      });
  }

  function uploadFiles(files: UploadSelectorFileUploadModel[], folders: UploadSelectorFolderUploadModel[], errorCallback: (e:any) => void) {
    files = files || [];
    folders = folders || [];

    const fileList = [...files, ...folders.flatMap(f => f.generateFileUploadModels())];

    const uploadModels: UploadModel[] =
      fileList.map(f => UploadModel.fromFile(f.file, UploadTypeResolver.getFromFileUploadType(f.type), f.path));

    const addedUploads = populateWithNewUploadModels(uploadModels);

    // Asynchronously create each upload on the server / generate upload token then start the uploads
    UploadService.authorizeUploads(uploadModels, requestId, requestType, metadataAvailable)
      .catch(e => {
        addedUploads.forEach(u => u.setFailedAuthorization());
        errorCallback(e);
      });
  }

  function populateWithNewUploadModels(newUploads: UploadModel[]): BaseUploadModel[] {
    const contextData = useContextDataStore();
    newUploads.forEach(u => {
    // Upload manager creates upload model before it's actually created on the backend so
    // some data can be missing. Put personaDisplayName manually for the correct displaying uploads in table.
      if (!u.personaDisplayName) {
        u.personaDisplayName = contextData.userData.displayName();
      }
    });

    const folderUploadModels = newUploads.filter(isUploadBelongsToFolder);

    const folderUploads = groupUploadsToFolders(folderUploadModels);
    const singleUploads = newUploads.filter(f => !isUploadBelongsToFolder(f));

    if (folderUploads.length > 0) {
      uploads.unshift(...folderUploads);
    }
    if (singleUploads.length > 0) {
      uploads.unshift(...singleUploads);
    }

    // Return upload proxies that are tracked by the Vue.
    return uploads.slice(0, folderUploads.length + singleUploads.length);
  }

  return { uploadFiles, uploadFilesObsolete };
}

